import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { t } from '../lib/i18n';
import { emptyFor } from './scope-notice';
import { messageFor } from '../lib/errors';
import { formatDate, formatNumber } from '../ui/format';
import { Panel } from '../ui/Page';
import { DataTable, type Column, type SortState } from '../ui/DataTable';
import { useCreateIntent } from '../app/create-intent';
import { ExportGlyph, FilterGlyph } from './BoardIcons';
import { FilterBuilder } from './FilterBuilder';
import { NewCompanyDialog } from './NewRecordDialog';
import { exportEntity, listFields, searchEntity, type CompanyRow, type FilterGroup } from './api';
import { listCatalogue } from '../settings/catalogues-api';

const EMPTY_FILTER: FilterGroup = { combinator: 'AND', conditions: [] };

/** Mirrors the server's own cap, so the message can name the number the file actually stopped at. */
const EXPORT_LIMIT = 5000;

/**
 * Companies list: server-side filtering, sorting and pagination (context.md section 134).
 *
 * The filter builder is what makes this screen its own rather than one more `EntityList`. Below it
 * is the same table every other list uses, so the column chooser, the quick filter, the page size
 * and the arrangement remembered on the account work here too — and the sort still travels to the
 * server, because re-ordering the page the browser happens to hold would answer a different
 * question than the one the header asks.
 */
export function CompaniesPage(): React.JSX.Element {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterGroup>(EMPTY_FILTER);
  const [applied, setApplied] = useState<FilterGroup>(EMPTY_FILTER);
  const [sort, setSort] = useState<SortState>({ field: 'created_at', direction: 'desc' });
  const [creating, setCreating] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  // Arriving from the home screen's Criar menu opens the same dialog the button here opens.
  useCreateIntent(() => setCreating(true));

  const fields = useQuery({
    queryKey: ['fields', 'companies'],
    queryFn: () => listFields('companies'),
  });

  // The catalogues, so a status id becomes the name and colour the tenant chose for it.
  const statuses = useQuery({
    queryKey: ['catalogue', 'customer-statuses'],
    queryFn: () => listCatalogue('customer-statuses'),
  });

  const sources = useQuery({
    queryKey: ['catalogue', 'customer-sources'],
    queryFn: () => listCatalogue('customer-sources'),
  });

  const active = applied.conditions.length > 0 ? applied : undefined;

  const results = useQuery({
    queryKey: ['companies', applied, sort],
    queryFn: () =>
      searchEntity<CompanyRow>('companies', {
        filter: active,
        sort,
        limit: 25,
        includeCount: true,
      }),
  });

  // The same filter and order the screen is showing, so the file and the list agree.
  const exporting = useMutation({
    mutationFn: () => exportEntity('companies', { ...(active ? { filter: active } : {}), sort }),
    onSuccess: (outcome) => {
      setExportError(
        outcome.truncated ? t('crm.exportTruncated').replace('{n}', String(EXPORT_LIMIT)) : null,
      );
    },
    onError: (caught: unknown) => setExportError(messageFor(caught)),
  });

  const columns: Column<CompanyRow>[] = [
    {
      key: 'name',
      header: t('crm.field.name'),
      sortField: 'name',
      render: (company) => <span className="strong">{company.name}</span>,
    },
    {
      /*
       * No `sortField` on either catalogue column: the registry marks `status_id` and `source_id`
       * filterable but not sortable, and a header that asks for an order the server refuses is a
       * 422 dressed as a control.
       */
      key: 'status_id',
      header: t('catalogues.tab.statuses'),
      text: (company) => nameOf(company.status_id, statuses.data),
      render: (company) => <Entry id={company.status_id} entries={statuses.data} withSwatch />,
    },
    {
      key: 'source_id',
      header: t('catalogues.tab.sources'),
      text: (company) => nameOf(company.source_id, sources.data),
      render: (company) => <Entry id={company.source_id} entries={sources.data} />,
    },
    {
      key: 'industry',
      header: t('crm.field.industry'),
      sortField: 'industry',
      render: (company) => company.industry ?? '—',
    },
    {
      key: 'employee_count',
      header: t('crm.field.employees'),
      sortField: 'employee_count',
      align: 'end',
      render: (company) =>
        company.employee_count === null ? '—' : formatNumber(company.employee_count),
    },
    {
      key: 'created_at',
      header: t('crm.field.createdAt'),
      sortField: 'created_at',
      secondary: true,
      render: (company) => <span className="muted small">{formatDate(company.created_at)}</span>,
    },
    // Off by default: real fields people sort and scan by, but not worth six columns of room.
    {
      key: 'email',
      header: t('crm.field.email'),
      hidden: true,
      render: (company) => company.email ?? '—',
    },
    {
      key: 'score',
      header: t('crm.field.score'),
      hidden: true,
      align: 'end',
      sortField: 'score',
      render: (company) => (company.score === null ? '—' : formatNumber(Number(company.score))),
    },
  ];

  const total = results.data?.total ?? null;

  return (
    <div className="view view--wide leads-page">
      <header className="board-head">
        <div>
          <nav className="breadcrumb" aria-label={t('crm.companies')}>
            <span>{t('nav.crm')}</span>
            <span aria-hidden="true">/</span>
            <span className="strong">{t('crm.companies')}</span>
          </nav>

          <h1>{t('crm.companies')}</h1>
          <p className="muted">{t('crm.companiesDescription')}</p>
        </div>

        <div className="cell-inline">
          <button
            type="button"
            className="button button--primary"
            onClick={() => setCreating(true)}
          >
            + {t('crm.newCompany')}
          </button>
        </div>
      </header>

      {exportError && (
        <p className="alert" role="alert">
          {exportError}
        </p>
      )}

      <Panel>
        {fields.data && (
          <FilterBuilder fields={fields.data.fields} value={filter} onChange={setFilter} />
        )}

        <div className="filters__actions">
          <button
            type="button"
            className="button button--primary button--compact"
            onClick={() => setApplied(filter)}
          >
            <FilterGlyph />
            {t('filters.apply')}
          </button>
          <button
            type="button"
            className="button button--quiet button--compact"
            onClick={() => {
              setFilter(EMPTY_FILTER);
              setApplied(EMPTY_FILTER);
            }}
          >
            {t('filters.clear')}
          </button>
        </div>
      </Panel>

      <Panel>
        <div className="panel-toolbar">
          <div className="panel-toolbar__title">
            {total !== null && (
              <span className="muted small">
                {t('crm.results').replace('{count}', String(total))}
              </span>
            )}
          </div>

          <div className="panel-toolbar__actions">
            <button
              type="button"
              className="button button--quiet button--compact"
              disabled={exporting.isPending}
              onClick={() => exporting.mutate()}
            >
              <ExportGlyph />
              {t('crm.export')}
            </button>
          </div>
        </div>

        <DataTable
          tableKey="companies"
          rows={results.data?.results}
          columns={columns}
          rowKey={(company) => company.id}
          isLoading={results.isPending}
          error={results.error}
          onRetry={() => void results.refetch()}
          onRowClick={(company) => navigate(`/crm/registro/companies/${company.id}`)}
          sort={{ value: sort, onChange: setSort }}
          empty={emptyFor(results.data?.visibility, {
            title: t('crm.empty'),
            description: t('crm.emptyBody'),
          })}
        />
      </Panel>

      {creating && <NewCompanyDialog onClose={() => setCreating(false)} />}
    </div>
  );
}

/** A catalogue entry's name, for the quick filter to match on. */
function nameOf(id: string | null, entries: { id: string; name: string }[] | undefined): string {
  return (id ? entries?.find((entry) => entry.id === id)?.name : '') ?? '';
}

/** A catalogue entry by id: its name, in its colour. An id with no entry is simply a dash. */
function Entry({
  id,
  entries,
  withSwatch = false,
}: {
  id: string | null;
  entries: { id: string; name: string; color: string }[] | undefined;
  withSwatch?: boolean;
}): React.JSX.Element {
  const entry = id ? entries?.find((candidate) => candidate.id === id) : undefined;

  if (!entry) return <span className="muted">—</span>;

  return (
    <span className="cell-inline">
      {withSwatch && <span className="swatch" style={{ background: entry.color }} aria-hidden />}
      <span>{entry.name}</span>
    </span>
  );
}
